import { useMutationWithToast } from '@pesto/ui';
import { Button, Typography } from '@pesto/ui';
import { CheckCircle } from 'lucide-react';

import type { Program } from '../../../__generated__/graphql/api';
import type { ModalProps, ProgramModalPayload } from '../../types/modal.types';

import { ListProgramsDocument, useUpdateProgramMutation } from '../../../__generated__/graphql/api';

export const ActivateProgramComponent = ({
  payload,
  api,
}: ModalProps<ProgramModalPayload & { twispJournalId?: Program['twispJournalId'] }, true>) => {
  const { programId, programName, twispJournalId } = payload;
  const { handleMutation } = useMutationWithToast();

  const [updateProgram, { loading }] = useUpdateProgramMutation({
    refetchQueries: [{ query: ListProgramsDocument }],
  });

  const handleConfirmActivate = () => {
    void handleMutation({
      mutation: () =>
        updateProgram({
          variables: {
            programId,
            active: true,
          },
        }),
      extractSuccess: (result) => !!result?.data?.updateProgram,
      debugLabel: 'activate-program',
      toast: {
        success: 'Program activated successfully',
        error: 'Failed to activate program',
        loading: 'Activating program...',
      },
      onSuccess: () => {
        api.close(true);
      },
    });
  };

  const handleCancel = () => {
    api.cancel();
  };

  return (
    <div className="space-y-6">
      {}
      <div className="flex items-center gap-3">
        <div className="bg-success/10 flex h-12 w-12 items-center justify-center rounded-full">
          <CheckCircle className="text-success h-6 w-6" />
        </div>
        <div>
          <Typography variant="headerSmall" className="font-semibold">
            Activate Program
          </Typography>
          <Typography variant="bodySmall" className="text-muted-foreground">
            Make this program available for use
          </Typography>
        </div>
      </div>

      {}
      <div className="space-y-3">
        <Typography variant="body">Are you sure you want to activate the following program?</Typography>

        <div className="bg-muted/50 rounded-lg border p-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <Typography variant="bodySmall" className="text-muted-foreground">
                Program Name:
              </Typography>
              <Typography variant="bodySmall" className="font-medium">
                {programName}
              </Typography>
            </div>
            <div className="flex justify-between">
              <Typography variant="bodySmall" className="text-muted-foreground">
                Program ID:
              </Typography>
              <Typography variant="bodySmall" className="font-mono font-medium">
                {programId}
              </Typography>
            </div>
            <div className="flex justify-between gap-3">
              <Typography variant="bodySmall" className="text-muted-foreground">
                Twisp Journal ID:
              </Typography>
              <Typography variant="bodySmall" className="font-mono font-medium">
                {twispJournalId ?? 'Missing'}
              </Typography>
            </div>
          </div>
        </div>

        <div className="border-success/20 bg-success/5 rounded-lg border p-4">
          <Typography variant="bodySmall" className={twispJournalId ? 'text-success' : 'text-warning'}>
            <strong>{twispJournalId ? 'Info' : 'Missing generated journal'}:</strong>{' '}
            {twispJournalId
              ? 'Activating this program will make it available for use in the system. You can deactivate it again later if needed.'
              : 'This program has no generated ledger resources yet. Activation needs backend provisioning for missing ledger resources.'}
          </Typography>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmActivate}
          disabled={!twispJournalId}
          loading={loading}
          className="bg-primary hover:bg-primary/90"
        >
          Activate Program
        </Button>
      </div>
    </div>
  );
};
